import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { marked } from 'marked';

// Configure marked for clean output
marked.setOptions({ breaks: true, gfm: true });

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost]     = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`/api/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(d => {
        setPost(d);
        if (d.meta?.title) document.title = `${d.meta.title} | NeuroRank`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Fetch all posts for "Read more" links
    fetch('/api/blog')
      .then(r => r.json())
      .then(d => setRelated((d.posts || []).filter(p => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, [slug]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <h1 style={{ fontSize: 48, fontWeight: 900, color: 'var(--accent)', marginBottom: 12 }}>404</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>Post not found.</p>
        <Link to="/blog" className="btn btn--primary">← Back to Blog</Link>
      </div>
    );
  }

  const html = marked(post.content || '');

  return (
    <div className="container" style={{ paddingTop: 48, paddingBottom: 80, maxWidth: 740 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Breadcrumb */}
        <div style={{ marginBottom: 32 }}>
          <Link
            to="/blog"
            style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            ← Blog
          </Link>
        </div>

        {/* Meta */}
        {post.meta?.date && (
          <p style={{ fontSize: 11, letterSpacing: 2, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
            {post.meta.date}
          </p>
        )}

        <h1 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, lineHeight: 1.2, marginBottom: 16 }}>
          {post.meta?.title || slug}
        </h1>

        {post.meta?.description && (
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: 32, borderLeft: '3px solid var(--accent)', paddingLeft: 16 }}>
            {post.meta.description}
          </p>
        )}

        {/* Keywords */}
        {post.meta?.keywords && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 32 }}>
            {post.meta.keywords.split(',').slice(0, 4).map(k => (
              <span key={k} style={{
                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                border: '1px solid var(--border)', color: 'var(--text-muted)',
              }}>
                {k.trim()}
              </span>
            ))}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 32 }} />

        {/* Content */}
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            fontSize: 15,
          }}
        />

        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '48px 0 32px' }} />

        {/* Share */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Share:</span>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent((post.meta?.title || 'Check this out') + ' via @NeuroRank')}&url=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
              borderRadius: 8, border: '1px solid #1d9bf033', color: '#1d9bf0',
              fontSize: 12, fontWeight: 600, textDecoration: 'none',
              background: '#1d9bf008', transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1d9bf018'}
            onMouseLeave={e => e.currentTarget.style.background = '#1d9bf008'}
          >
            Share on X
          </a>
          <button
            onClick={() => navigator.clipboard.writeText(window.location.href)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
              borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text-muted)',
              fontSize: 12, fontWeight: 600, background: 'none', cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            Copy Link
          </button>
        </div>

        {/* CTA */}
        <div className="card" style={{ textAlign: 'center', padding: '32px 24px' }}>
          <p style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 700, marginBottom: 8 }}>
            TEST YOUR OWN COGNITIVE PROFILE
          </p>
          <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>
            Find your archetype in 10 minutes
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>
            Reaction time · Aim precision · Decision-making · Composure · Tilt resistance
          </p>
          <Link to="/" className="btn btn--primary" style={{ padding: '12px 28px', fontSize: 15 }}>
            Take the Combine →
          </Link>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <div style={{ marginTop: 48 }}>
            <p style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: 16 }}>
              More from the Blog
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  style={{
                    display: 'block', padding: '14px 18px', borderRadius: 10,
                    border: '1px solid var(--border)', textDecoration: 'none',
                    background: 'var(--bg-card)', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{r.title}</div>
                  {r.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{r.description}</div>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
